import { saveAs } from "file-saver";

const FILENAME_PATTERN = /filename="([^"]+)"/;

/**
 * The file name S3 would save the body under. `S3Service.getSignedDownloadUrl`
 * presigns `Content-Disposition: attachment; filename="…"`, which S3 carries in
 * the url as `response-content-disposition`. Read it from there rather than the
 * response header: a cross-origin response only exposes the headers its CORS
 * rule lists. Falls back to the object key's last segment.
 */
const fileNameOf = (url: string): string => {
  const { pathname, searchParams } = new URL(url);
  const disposition = searchParams.get("response-content-disposition") ?? "";
  return (
    FILENAME_PATTERN.exec(disposition)?.[1] ??
    decodeURIComponent(pathname.split("/").pop() ?? "")
  );
};

/**
 * Download a finished bulk action's presigned url without a user gesture — the
 * host calls it from `onSettled`, seconds after the click that started the
 * action.
 *
 * The file is fetched and handed to `saveAs` as a blob, the way the rest of the
 * apps save presigned files. `saveAs` given the url itself would be worse here:
 * cross-origin it probes CORS with a synchronous request, and on a miss opens a
 * `_blank` window — which the popup blocker rejects this far from a gesture.
 * Rejects when S3 does, so the host can report it; the panel's link stays as the
 * way back to the file.
 */
export const startDownload = async (url: string): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  saveAs(await response.blob(), fileNameOf(url));
};
